import { AiChat } from '../common/ai-chat/aiChatTypes';
import { AiChatMessages } from '../common/ai-chat/AiChatMessages';
import { Box, Stack } from '@mui/material';
import { useAiAgentMessages } from './useAiAgentMessages';
import { IconButton } from '@sweep-io/sweep-design';
import { ArrowDown } from '@sweep-io/sweep-design/dist/icons';
import useContainerWithScrollArrow from '../../hooks/useContainerWithScrollArrow';
import { AI_AGENTS_INFO } from './aiAgentsConsts';
import { AgentType } from '@server/ai';
import useAgentsChats from './useAgentsChats';
import { useSelector } from 'react-redux';
import { selectActiveChatId } from './agentsChatsReducer';
import AgentIssues from './AgentIssues';
import useAgentsHome from './useAgentsHome';
import { DataTableVariant } from '../common/table/TableTypes';
import { useAgentChatInstance } from './AgentChatInstanceContext';

interface AiAgentScrollAreaProps {
  maxWidth?: string;
  searchTermToHighlight?: string;
  onTextSelection?: (text: string | null) => void;
  chatData: AiChat;
  hideFirstMessage?: boolean;
  scrollAreaPadding?: number;
  crmOrgId: string;
}

const PADDING_BOTTOM = 32;

const AgentChatScrollArea = ({
  maxWidth,
  searchTermToHighlight,
  onTextSelection,
  chatData,
  hideFirstMessage,
  scrollAreaPadding,
  crmOrgId,
}: AiAgentScrollAreaProps) => {
  const { activeAgent, handleAnimationProgressPersistence } = useAgentsChats();
  const { chatInstanceId } = useAgentChatInstance();
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const { isExpanded } = useAgentsHome();
  const {
    aiChatDetails,
    isStreamingResponse,
    name: chatName,
    isLoadingResponse,
    id: chatId,
  } = chatData;
  const { messages: _messages } = aiChatDetails;
  const messages = hideFirstMessage ? _messages.slice(1) : _messages;

  const dataTableVariant = isExpanded ? DataTableVariant.medium : DataTableVariant.small;

  const { isSupportIssues } = AI_AGENTS_INFO[activeAgent?.type ?? AgentType.Documentation];
  const hasBestPractices = activeAgent?.bestPractices && activeAgent.bestPractices.length > 0;

  const { renderInitialFetchMessage, messagesToRender } = useAiAgentMessages({
    messages,
    agentType: activeAgent?.type ?? AgentType.Documentation,
  });

  const lastMessage =
    messagesToRender.length > 0 ? messagesToRender[messagesToRender.length - 1] : null;

  const lastMessageContent =
    (lastMessage?.content ?? '') + (lastMessage?.reasoningChunks?.join('') ?? '');

  const { showScrollButton, scrollToBottom, containerRef } = useContainerWithScrollArrow({
    recalculateWhenThisChange: lastMessageContent,
  });

  const renderResponseLoader = !renderInitialFetchMessage && Boolean(isLoadingResponse);

  if (!activeAgent) return null;

  return (
    <Stack position="relative" overflow="hidden" flex={1} width="100%">
      <Box
        ref={containerRef}
        id={`chat-messages-container-${chatId}`}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          flex: 1,
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Stack
          padding={(scrollAreaPadding ?? 12) + 'px'}
          gap={2}
          paddingBottom={PADDING_BOTTOM + 'px'}
          paddingTop="24px"
          sx={{
            maxWidth,
            width: '100%',
            flex: 1,
            justifyContent: 'start',
          }}
        >
          {isSupportIssues && hasBestPractices && <AgentIssues agent={activeAgent} />}

          <Stack gap={2}>
            <AiChatMessages
              activeChatId={activeChatId}
              messages={messagesToRender}
              renderLoader={renderResponseLoader}
              containerRef={containerRef}
              searchTermToHighlight={searchTermToHighlight}
              renderInitialFetchMessage={renderInitialFetchMessage}
              renderChatPaywallBlockMessage={false}
              onTextSelection={onTextSelection}
              isStreamingResponse={isStreamingResponse}
              chatName={chatName}
              dataTableVariant={dataTableVariant}
              spacerFix={PADDING_BOTTOM}
              onAnimationProgressPersistence={handleAnimationProgressPersistence}
              crmOrgId={crmOrgId}
            />
          </Stack>
        </Stack>
      </Box>
      {showScrollButton && (
        <Stack sx={{ position: 'absolute', bottom: '16px', width: '100%', alignItems: 'center' }}>
          <IconButton onClick={scrollToBottom} variant="system" size="small">
            <ArrowDown />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
};

export default AgentChatScrollArea;
